import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Code,
  Building,
  ChevronRight,
  Brain,
  Network,
} from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import CardMetadata from '../components/common/CardMetadata';
import { lldProgressService, LLDProgressSummary } from '../services/lldProgressService';
import { machineCodingProjectService } from '../services/machineCodingProjectService';
import { dsaService } from '../services/dsaService';
import { useAuth } from '../context/AuthContext';
import { isMachineCodingEnabledFor } from '../config/featureAccess';

interface PracticeCard {
  title: string;
  description: string;
  icon: React.ElementType;
  link: string;
  badge: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  modulesCount: number;
  features: string[];
  progress?: string;
  progressPercent?: number;
}

const Practice: React.FC = () => {
  const { state: authState } = useAuth();
  const machineCodingEnabled = isMachineCodingEnabledFor(authState.user?.enabledFeatures);
  const [lldProgress, setLldProgress] = useState<LLDProgressSummary | null>(null);
  const [dsaSolved, setDsaSolved] = useState<number | null>(null);
  const [mcCompleted, setMcCompleted] = useState<number | null>(null);
  const [mcTotal, setMcTotal] = useState<number | null>(null);

  useEffect(() => {
    // LLD progress
    lldProgressService.getProgressSummary()
      .then(setLldProgress)
      .catch(() => setLldProgress(null));

    // DSA progress — count completed problems from user progress
    dsaService.getAllProgress()
      .then((progress: any[]) => {
        const solved = progress.filter((p: any) => p.isCompleted).length;
        setDsaSolved(solved);
      })
      .catch(() => setDsaSolved(null));

    // MC progress — count completed projects
    machineCodingProjectService.listMine()
      .then((projects) => {
        const completed = projects.filter((p) => p.status === 'completed').length;
        setMcCompleted(completed);
        setMcTotal(projects.length);
      })
      .catch(() => { setMcCompleted(null); setMcTotal(null); });
  }, []);

  const lldProgressText = lldProgress
    ? `${lldProgress.completedProblems}/${lldProgress.totalProblems} completed`
    : undefined;

  const dsaProgressText = dsaSolved !== null
    ? `${dsaSolved} solved`
    : undefined;

  const mcProgressText = mcTotal !== null && mcTotal > 0
    ? `${mcCompleted}/${mcTotal} completed`
    : undefined;

  const cards: PracticeCard[] = [
    {
      title: 'DSA Practice',
      description: 'Practice curated problems across arrays, trees, graphs, dynamic programming, and more.',
      icon: BarChart3,
      link: '/practice/dsa',
      badge: '250 problems',
      difficulty: 'intermediate',
      estimatedTime: '60+ hours',
      modulesCount: 14,
      features: ['LetsBoostUp 75 — quick prep', 'LetsBoostUp 150 — comprehensive', 'LetsBoostUp 250 — mastery', 'Pattern-based tracking'],
      progress: dsaProgressText,
      progressPercent: dsaSolved !== null ? Math.round((dsaSolved / 250) * 100) : 0,
    },
    {
      title: 'Machine Coding',
      description: 'Build complete applications in a live IDE that mirrors real machine-coding rounds.',
      icon: Code,
      link: '/practice/machine-coding',
      badge: 'Live coding',
      difficulty: 'advanced',
      estimatedTime: '15+ hours',
      modulesCount: 5,
      features: ['In-browser IDE', 'Real Node.js environment', 'API testing built-in', 'Clean architecture templates'],
      progress: mcProgressText,
      progressPercent: mcTotal && mcTotal > 0 ? Math.round(((mcCompleted || 0) / mcTotal) * 100) : 0,
    },
    {
      title: 'LLD Practice',
      description: 'Design object-oriented systems through a guided eight-step interview workflow.',
      icon: Building,
      link: '/practice/lld',
      badge: 'Interview style',
      difficulty: 'intermediate',
      estimatedTime: '10+ hours',
      modulesCount: 5,
      features: ['Curated problems + your own', 'Object-oriented design', 'SOLID principles', 'Design patterns applied'],
      progress: lldProgressText,
      progressPercent: lldProgress ? Math.round((lldProgress.completedProblems / lldProgress.totalProblems) * 100) : 0,
    },
    {
      title: 'HLD System Design',
      description: 'Learn how systems evolve at scale, then practise designing architectures you can defend.',
      icon: Network,
      link: '/practice/hld',
      badge: 'System design',
      difficulty: 'intermediate',
      estimatedTime: '20+ hours',
      modulesCount: 15,
      features: ['Foundation articles', 'Interview reasoning', 'Architecture workspace', 'Design trade-offs'],
      progressPercent: 0,
    },
  ];

  const getDifficultyBadgeClass = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'badge-green';
      case 'intermediate':
        return 'badge-yellow';
      case 'advanced':
        return 'badge-red';
      default:
        return 'badge-gray';
    }
  };

  return (
    <div className="nav-tab-page practice-page min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Shared PageHeader — same as SystemDesign / Languages */}
        <PageHeader
          title="Practice Arena"
          description="Sharpen your skills with DSA problem sets, machine-coding challenges, low-level design, and high-level system design."
          icon={Brain}
          iconColor="text-orange-600 dark:text-orange-400"
          gradientColors={{
            from: 'from-orange-500',
            via: 'via-rose-500',
            to: 'to-purple-500',
          }}
        />

        {/* Cards — matching SectionCard 'detailed' variant style */}
        <div className="practice-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.filter((card) => card.title !== 'Machine Coding' || machineCodingEnabled).map((card) => {
            const Icon = card.icon;
            return (
              <Link
                key={card.title}
                to={card.link}
                aria-label={`Start ${card.title}`}
                className="catalog-card group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-4 focus-visible:ring-offset-gray-900"
              >
                {/* Card Header */}
                <div className="practice-card-header flex items-start gap-3 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-100 to-sky-100 dark:from-primary-900/30 dark:to-sky-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {card.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mt-1.5">
                      {card.description}
                    </p>
                  </div>
                </div>

                {/* Metrics — same hierarchy as language cards */}
                <CardMetadata
                  items={[
                    { label: 'Modules', value: card.modulesCount > 0 ? card.modulesCount : null },
                    { label: 'Time', value: card.estimatedTime },
                  ]}
                />

                {/* Footer — CTA */}
                <div className="catalog-card__footer practice-card-footer flex items-center justify-between gap-3 pt-4 mt-auto border-t border-gray-100 dark:border-gray-800">
                  <span className={`practice-difficulty px-2.5 py-1 rounded-md text-[11px] font-semibold ${getDifficultyBadgeClass(card.difficulty)}`}>
                    {card.difficulty.charAt(0).toUpperCase() + card.difficulty.slice(1)}
                  </span>
                  <div className="flex items-center text-primary-600 dark:text-primary-400 group-hover:translate-x-1 transition-transform rounded-md">
                    <span className="text-sm font-medium mr-1">Start</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Practice;
